import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AgmMap } from '@agm/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Modal } from '../../../../classes/modal';
import { Coordinates } from '../../../../classes/Coordinates';
import { FormValidation } from '../../../../classes/formValidation';
import { eDialogViews, IIdNamePair } from '../../../../interfaces/abstract';
import { eSoilWebCrawErrors } from '../../../../interfaces/constants';
import { CMError } from '../../../../interfaces/interfaces';
import { MapControlDirective } from '../../../../directives/map-control.directive';
import { ValidateService } from '../../../../services/validate.service';
import { UpdateService } from '../../../../services/update.service';
import { DeleteConfirmationComponent, DeleteConfirmationSettings } from '../../../shared/dialogs/delete-confirmation';
import { SharedKeepDiscardDialog } from '../../../shared/dialogs/keep-discard-dialog';
import { SharedUpdateService } from '../../../shared/dialogs/update.service';
import { IAgmMapClick, ICoordinates } from '../../../shared/interfaces';
import { RanchService } from '../../service';
import { IRanchBasicInfoViewModel } from '../../interfaces';
import { ILotEdit } from '../../modals/lot/interfaces';
import { CustomSoilPropertiesFormBuilder } from '../../../soil-properties-table/customSoilPropertiesFormBuilder';
import { SoilTypeService } from '../../../../models/soil-type/service';
import { MatDialog } from '@angular/material/dialog';

export type ILotFields = 'Id' | 'Name' | 'Acres' | 'Coordinates' |
	'ObstructionDepth' | 'ShouldUseObstructionDepth' | 'DefaultSoilTypeId';

@Component({
	moduleId: module.id,
	selector: 'ranch-lot-form',
	templateUrl: 'ranch-lot-form.html',
	styleUrls: ['ranch-lot-form.scss']
})

export class RanchLotFormComponent implements OnInit, OnDestroy {
	@Input()
	public id: number;

	@Input()
	public lot: ILotEdit;

	@Input()
	public ranchCoordinates: string;

	@Input()
	public isRanchCreateProcess: boolean;

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onDelete: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onHide: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onShow: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onSubmit: EventEmitter<ILotEdit> = new EventEmitter<ILotEdit>();

	public coordinateExplanation: string;
	public dialogViews = eDialogViews;
	public editingSoilType = false;
	public form: FormGroup;
	public isAssociatedWithPlanting: boolean = null;
	public loadingData = true;
	@ViewChild(AgmMap, { static: false }) public map: AgmMap;
	public markers: ICoordinates[] = new Array();
	public ranchLocation: ICoordinates;
	public soilTypeName: string;
	public soilError: eSoilWebCrawErrors;
	public view: eDialogViews;
	public viewCache: eDialogViews;

	private _basicInfo: IRanchBasicInfoViewModel;
	private _lat = 38.53957267203905;
	private _lng = -121.73841498792171;
	@ViewChild(MapControlDirective, { static: false }) private _mapControl: MapControlDirective;
	private _subscriptions$: Subject<boolean>;
	private _zoom = 12;

	public soilPropertiesFormBuilder: CustomSoilPropertiesFormBuilder;

	private _formValueEmpty: ILotEdit = {
		Id: null,
		Name: null,
		Acres: null,
		Coordinates: null,
		ObstructionDepth: null,
		SoilLayers: []
	};

	public get f(): { [key in ILotFields]: AbstractControl } {
		return this.form.controls as { [key in ILotFields]: AbstractControl };
	}

	constructor(
		private _dialog: MatDialog,
		private _fb: FormBuilder,
		private _ranchService: RanchService,
		private _sharedUpdateService: SharedUpdateService,
		private _soilTypeService: SoilTypeService,
		private _updateService: UpdateService,
		private _validateService: ValidateService
	) { }

	public ngOnInit(): void {
		this._subscriptions$ = new Subject();
		this.ranchLocation = {
			lat: null,
			lng: null
		};
		this.soilError = null;

		if (this.id) {
			this.view = eDialogViews.UPDATE;
			this._detectAssociatedPlantings(this.id);
			this._getRanchData();
			this._getEditLotData(this.id);
		} else if (this.lot) { // editing during ranch create process
			this.view = eDialogViews.UPDATE;
			this._setRanchCoordinates(this.ranchCoordinates);
			this._initializeForm(this.lot);
			this._initializeSoilPropertiesForm(this.lot);
			this.loadingData = false;
			this._resetMap();
		} else {
			this.view = eDialogViews.CREATE;
			this._setRanchCoordinates(this.ranchCoordinates);
			this._initializeForm(this._formValueEmpty);
			this._initializeSoilPropertiesForm(this._formValueEmpty);
			this.loadingData = false;
			this._resetMap();
		}

		this._setupPopupSubscriptions();
	}

	public ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public cancel(): void {
		if (this.form && this.form.touched) {
			this._dialog.open(SharedKeepDiscardDialog, {
				width: '500px',
				disableClose: true,
			});

			this.onHide.emit();
		} else {
			this.onCancel.emit();
		}
	}

	public isLotValid(): boolean {
		if (!this.soilPropertiesFormBuilder || !this.soilPropertiesFormBuilder.form ||
			this.soilPropertiesFormBuilder.form.invalid) {

			return false;
		}

		if (this.soilError) {
			return false;
		}

		return this.form && this.form.valid;
	}

	public lotCoordsChanged(): void {
		if (!this.f.Coordinates.valid) {
			return;
		}

		this._placeMarkerString(this.f.Coordinates.value);
		this.f.Coordinates.markAsTouched();

		this.soilPropertiesFormBuilder.initializeWithCoordinates(this.f.Coordinates.value,
			this._soilTypeService).then((res) => {
				this.soilError = res;

				if (this.soilError) {
					this.soilTypeName = null;
					if (this.soilError === eSoilWebCrawErrors.OutOfRange) {
						this.coordinateExplanation = `The coordinates are outside
							the service area. Please enter coordinates within the US.`;
					} else if (this.soilError === eSoilWebCrawErrors.ServerFailure) {
						this.coordinateExplanation = `The soil data service is
							currently unavailable. Please try again later.`;
					} else if (this.soilError === eSoilWebCrawErrors.ParseError) {
						this.coordinateExplanation = `Unable to retrieve soil
							data for this location. Please try a slightly different
							location..`;
					}
				} else {
					this.soilTypeName = this.soilPropertiesFormBuilder.soilWebProperties.Record.Name;
					this.coordinateExplanation = null;
				}
			}
		);
	}

	public mapClicked(event: IAgmMapClick): void {
		let coords: Coordinates;

		this.soilError = null;
		this.coordinateExplanation = null;
		coords = new Coordinates(event.coords.lat + ',' + event.coords.lng);

		this.f.Coordinates.setValue(coords.toString());
	}

	public mapReady(): void {
		this._resetMap();
	}

	public delete(e: MouseEvent): boolean {
		let data: DeleteConfirmationSettings;

		if (this.isAssociatedWithPlanting) {
			return false;
		}

		if (!this.form) {
			return false;
		}

		data = {
			specificName: this.f.Name.value,
			objectName: null,
			additionalMessage: null,
		};

		this._dialog.open(DeleteConfirmationComponent, {
			data: data,
			disableClose: true,
		});

		this.onHide.emit();

		if (e) {
			e.stopPropagation();
		}

		return true;
	}

	public showMap(): void {
		this.editingSoilType = false;
	}

	public showSoilProperties(): void {
		this.editingSoilType = true;
	}

	public soilListContains(soilList: IIdNamePair[], id: number): boolean {
		for (let soil of soilList) {
			if (soil.Id === id) {
				return true;
			}
		}
		return false;
	}

	public submit(): void {
		let lot: ILotEdit;

		if (!this.form || this.form.invalid) {
			return;
		}

		if (!this.soilPropertiesFormBuilder.form) {
			return;
		}

		lot = this.form.value;
		lot = this._roundInputs(lot);
		lot.SoilLayers = this.soilPropertiesFormBuilder.cullRedundantFields();
		lot.SoilWebProperties = this.soilPropertiesFormBuilder.soilWebProperties;

		this.onSubmit.emit(lot);
	}

	public toggleObstructionDepth(): void {
		if (!this.f) {
			return;
		}

		if (!this.f.ShouldUseObstructionDepth.value) {
			this.f.ObstructionDepth.setValue(null);
		}
	}

	private _bindValueChanges(): void {
		this.f.ShouldUseObstructionDepth.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			if (this.f.ShouldUseObstructionDepth.value) {
				this.f.ObstructionDepth.setValidators([Validators.min(0.0000001), Validators.max(3.9999999)]);
			} else {
				this.f.ObstructionDepth.clearValidators();
				this.f.ObstructionDepth.setErrors(null);
			}
		});

		this.f.Coordinates.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.lotCoordsChanged();
		});
	}

	private _detectAssociatedPlantings(ranchLotId: number): Promise<void> {
		return this._ranchService.isUsed(ranchLotId, this._updateService.currentRanchId).then(res => {
			this.isAssociatedWithPlanting = res;
		});
	}

	private _getEditLotData(id: number): Promise<ILotEdit> {
		return this._ranchService.getEditRanchLotData(id)
		.then(data => {
			if (!data) {
				return;
			}

			data.Id = id;
			this._initializeForm(data);
			this._initializeSoilPropertiesForm(data);

			this.form.markAsDirty();
			this.loadingData = false;

			this._resetMap();

			return data;
		});
	}

	private _getRanchData(): Promise<void> {
		return this._ranchService.getRanchBasicInfo()
		.then((res) => {
			if (!res) {
				return;
			}

			this._basicInfo = res;

			if (this._basicInfo.Coordinates) {
				this.ranchLocation = {
					lat: Number(this._basicInfo.Coordinates.split(',')[0]),
					lng: Number(this._basicInfo.Coordinates.split(',')[1])
				}
			}
		});
	}

	private _initializeForm(data: ILotEdit): void {
		this.form = this._fb.group({
			Id: [data.Id],
			Name: [data.Name, [Validators.required, Validators.maxLength(128)]],
			Acres: [data.Acres, [Validators.required, Validators.min(0.001),
				Validators.max(10000)]],
			ShouldUseObstructionDepth: [data.ObstructionDepth > 0],
			ObstructionDepth: [data.ObstructionDepth, [Validators.min(0.0000001), Validators.max(3.9999999)]],
			Coordinates: [data.Coordinates, { validators: [Validators.required,
				FormValidation.CoordinateStringIsValid(), this._validateBadLatLng(),
				this._validateNoSoilData(), this._validateHTTPError()], updateOn: 'blur'}],
		});

		this._bindValueChanges();
	}

	private _initializeSoilPropertiesForm(data: ILotEdit): void {
		this.soilPropertiesFormBuilder = new CustomSoilPropertiesFormBuilder(this._fb,
			data.SoilLayers, this._subscriptions$);

		if (data.SoilWebProperties) {
			if (data.SoilWebProperties.Error) {
				this.soilError = data.SoilWebProperties.Error;
				this.soilTypeName = null;
			} else {
				this.soilPropertiesFormBuilder.initializeWithStaticData(data.SoilWebProperties);
				this.soilTypeName = data.SoilWebProperties.Record.Name;
			}
		}

		this.f.ShouldUseObstructionDepth.setValue(data.ObstructionDepth > 0);
	}

	private _placeMarker(lat: number, lng: number): void {
		this.markers = new Array();

		this.markers.push({
			lat: Number(lat),
			lng: Number(lng)
		});
	}

	private _placeMarkerString(coords?: string): void {
		let latLng: string[];

		if (coords && this._validateService.validateCoordinatesString(coords)) {
			this.markers = new Array();
			latLng = coords.split(',');
			this._placeMarker(Number(latLng[0]), Number(latLng[1]));
		}
	}

	private _recenterMap(lat: number, lng: number, zoom: number): void {
		this._mapControl.centerMap(lat, lng);
		this._mapControl.setZoom(zoom);
	}

	private _resetMap(): void {
		if (this.f && this.f.Coordinates.value) {
			this._setMap(this.f.Coordinates.value);
		} else if (this._basicInfo && this._basicInfo.Coordinates) {
			this._setMap(this._basicInfo.Coordinates);
		}

		Modal.resize();
	}

	private _roundInputs(x: ILotEdit): ILotEdit {
		x.Acres = this._validateService.convertToFloat(x.Acres, 2);

		return x;
	}

	private _setMap(coords: string): void {
		if (!this.map) {
			return;
		}

		this.map.triggerResize().then(event => {
			let lat, lng: number;

			if (!coords || coords === null || coords === undefined || !this._validateService.validateCoordinatesString(coords)) {
				this._recenterMap(this._lat, this._lng, this._zoom);
				return;
			}

			lat = Number(coords.split(',')[0]);
			lng = Number(coords.split(',')[1]);

			this._recenterMap(lat, lng, this._zoom);

			if (this.view === eDialogViews.UPDATE) {
				this._placeMarker(lat, lng);
			}
		});
	}

	private _setRanchCoordinates(coordinates: string): void {
		let coords: Coordinates;

		this._basicInfo = {
			Coordinates: coordinates
		} as IRanchBasicInfoViewModel;

		if (!coordinates) {
			return;
		}

		coords = new Coordinates(coordinates);

		if (!coords) {
			return;
		}

		this.ranchLocation = {
			lat: coords.lat,
			lng: coords.lng
		};
	}

	private _setupPopupSubscriptions(): void {
		this._sharedUpdateService.keep$
			.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onShow.emit();
		});

		this._sharedUpdateService.discard$
			.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onCancel.emit();
		})

		this._sharedUpdateService.delete$
			.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onDelete.emit();
		});

		this._sharedUpdateService.cancel$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onShow.emit();
		});
	}

	private _validateBadLatLng(): ValidatorFn {
		return (control: AbstractControl): {[key: string]: any} | null => {
			let valid: boolean;

			valid = (this.soilError !== null && this.soilError === eSoilWebCrawErrors.OutOfRange) ?
				false : true;

			return valid ? null : {'badLatLng': {value: control.value}};
		};
	}

	private _validateHTTPError(): ValidatorFn {
		return (control: AbstractControl): {[key: string]: any} | null => {
			let valid: boolean;

			valid = (this.soilError !== null && this.soilError === eSoilWebCrawErrors.ServerFailure) ?
				false : true;

			return valid ? null : {'httpError': {value: control.value}};
		};
	}

	private _validateNoSoilData(): ValidatorFn {
		return (control: AbstractControl): {[key: string]: any} | null => {
			let valid: boolean;

			valid = (this.soilError !== null && this.soilError === eSoilWebCrawErrors.ParseError) ?
				false : true;

			return valid ? null : {'noSoilData': {value: control.value}};
		};
	}
}
